import {
  Box,
  useMediaQuery,
  Typography,
  Slide,
  IconButton,
} from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";
import Header from '../v2/TopNav/Header';
import Sidebar from '../v3/Sidebar';
import { useEffect, useMemo, useState } from 'react';
import { decodeToken } from '../utils/settings';
import BottomTabBar from '../v3/BottomTabs';
import { useDispatch, useSelector } from 'react-redux';
import { getTabConfig } from '../v3/BottomTabs/tabConfig';
import { getDashboardConfigData } from '../v3/Home/Action';
import { setDashboardConfig } from '../v3/Common/Dashboard/dasboardAction';
import {
  BackrouteProvider,
  useBackrouteContext,
} from "../contexts/BackrouteContext";
import { toast } from 'react-toastify';
import { FirmManufactureModal } from "../v3/Common/FirmManufactureModal";
import { fetchSuppliers } from '../v3/Common/Entity/actions/reduxActions';
import { EMPLOYEE_TYPES_ENUM } from "../v3/MyAccount/Employee/Enum";
import { MODULE_PERMISSION_ENUM } from "../v3/Sidebar/Enum";

const MainLayout = ({
  children,
  sidebarTitle = null,
  isPwa = false,
  backroute = null,
  moduleKey = null,
  isHomePage = false,
  showHeader = true,
  showMenu,
  showProfileIcon = true,
}) => {
  return (
    <BackrouteProvider>
      <MainLayoutInner
        sidebarTitle={sidebarTitle}
        isPwa={isPwa}
        backroute={backroute}
        moduleKey={moduleKey}
        isHomePage={isHomePage}
        showHeader={showHeader}
        showMenu={showMenu}
        showProfileIcon={showProfileIcon}>
        {children}
      </MainLayoutInner>
    </BackrouteProvider>
  );
};

const MainLayoutInner = ({
  children,
  sidebarTitle = null,
  isPwa = false,
  backroute = null,
  moduleKey = null,
  isHomePage = false,
  showHeader = true,
  showMenu,
  showProfileIcon = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = localStorage.getItem("token");
  const isMobile = useMediaQuery("(max-width: 1200px)");

  // Get context values from child components
  const {
    backroute: contextBackroute,
    title: contextTitle,
    customBackHandler,
    backOnly,
  } = useBackrouteContext();

  // Use context values if available, otherwise use props
  const effectiveBackroute = contextBackroute || backroute;
  const effectiveTitle = contextTitle || sidebarTitle;
  const navigationTarget = effectiveBackroute ? effectiveBackroute : -1;

  const [open, setOpen] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [firmOnboarded, setFirmOnboarded] = useState(true);
  const [firmModal, setFirmModal] = useState(false);
  const [manufacturerModal, setManufacturerModal] = useState(false);
  const handleBackNavigation = () => {
    if (customBackHandler) {
      customBackHandler();
    } else {
      navigate(navigationTarget);
    }
  };

  const handleProtectedTabChange = (newValue) => {
    if (customBackHandler && !backOnly) {
      customBackHandler();
    } else {
      navigate(newValue);
    }
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };
  useEffect(() => {
    const getTokenData = async () => {
      if (token) {
        try {
          const decoded = await decodeToken(token);
          setTokenData(decoded);
          setFirmOnboarded(decoded?.default_company_info);
        } catch (err) {
          console.error("Failed to decode token", err);
        }
      }
    };
    getTokenData();
  }, [token]);

  const showFirmModal = () => {
    setFirmModal(true);
  };

  const showManufacturerModal = () => {
    setManufacturerModal(true);
  };
const { loginUser } = useSelector((state) => ({
  loginUser: state.loginReducer.loginUser,
}));
const dashboardConfig = useSelector(
  (state) => state.dashboardReducer.dashboardConfig,
);
const allowedKeys = dashboardConfig?.menu_config?.map((item) => item.key) ?? [];
const isAllowed = (key) => {
  if (key === MODULE_PERMISSION_ENUM.PAYMENT && allowedKeys.includes(key)) {
    if (
      loginUser?.role === EMPLOYEE_TYPES_ENUM.STAFF &&
      !allowedKeys.includes(MODULE_PERMISSION_ENUM.MY_BUSINESS)
    )
      return true;
    return false;
  }
  if (
    key === MODULE_PERMISSION_ENUM.INVENTORY &&
    allowedKeys.includes(MODULE_PERMISSION_ENUM.MY_BUSINESS) &&
    allowedKeys.includes(MODULE_PERMISSION_ENUM.INVENTORY)
  ) {
    return false;
  }
  if (!Array.isArray(allowedKeys)) return true;
  return allowedKeys.includes(key);
};
const menuLabelMap = useMemo(() => {
  const map = {};
  if (dashboardConfig?.menu_config) {
    dashboardConfig.menu_config.forEach((item) => {
      map[item.key] = item.label;
    });
  }
  return map;
}, [dashboardConfig]);

const tabConfig = getTabConfig(isPwa, menuLabelMap, tokenData);
const TabConfig = tabConfig.filter((tab) => (tab.key === MODULE_PERMISSION_ENUM.HOME || isAllowed(tab.key)) && tab.visible);
  const {
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedSupplierData,
  } = useSelector((state) => state.entityReducer);

  const shouldFetchSuppliers = isPwa && (!selectedSupplierData || selectedSupplierData.length === 0);
  useEffect(() => {
    if (shouldFetchSuppliers) {
      dispatch(fetchSuppliers());
    }
  }, [shouldFetchSuppliers, dispatch]);
  
  const fetchDashboardConfig = async () => {
    const result = await getDashboardConfigData({
      manufacturer_id: selectedManufacturer || null,
    });
    const { error, data, message } = result;
    if (!error) {
      await dispatch(setDashboardConfig(data));
    } else {
      toast.error(message);
    }
  };
  useEffect(() => {
    if (loginUser) {
      fetchDashboardConfig();
    }
  }, [selectedManufacturer, selectedCompanyEntity, loginUser]);
  if (!token) {
    return <Navigate to="/login" />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Box
        id="main-scroll-container"
        sx={{
          width: "100%",
          maxWidth: "1920px",
          position: "relative",
          overflow: "scroll",
          height: { xs: "100vh", md: "100vh" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}>
        <Box
          sx={{
            position: "absolute",
            width: { xs: "650px", md: "686.31px", lg: "650px" },
            height: {
              xs: isHomePage ? "-webkit-fill-available" : "auto",
              md: "900.23px",
              xl: "-webkit-fill-available",
            },
            // height: { xs:'-webkit-fill-available',md: '900.23px', xl: '-webkit-fill-available' },
            display: "block",
            top: "-210.68px",
            left: "-315.54px",
            borderRadius: "4px",
            transform: "rotate(0deg)",
            backgroundImage: `url('https://d2djimvzddgguw.cloudfront.net/humbee-homepage-images/banner/hexagon-pattern.svg')`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1300,
            pb: { xs: 0, md: 2 },
            pt: { xs: 2 },
            px: { xs: 2 },
            display: { xs: showHeader ? "block" : "none", md: "block" },
            backgroundColor: "#FAFAFA",
          }}>
          <Header
            isHomePage={isHomePage}
            sidebarTitle={effectiveTitle}
            backroute={effectiveBackroute}
            toggleDrawer={toggleDrawer}
            isPwa={isPwa}
            showMenu={showMenu}
            moduleKey={moduleKey}
            onBackClick={handleBackNavigation}
            showProfileIcon={showProfileIcon}
          />
        </Box>

        <Box
          sx={{
            display: { md: "flex" },
            height: { xs: "100vh", md: "calc(100vh - 130px)" },
            overflow: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}>
          {isMobile && open && (
            <>
              <Slide
                direction="left"
                in={open}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 500, exit: 300 }}>
                <Box
                  sx={{
                    width: "300px",
                    display: "flex",
                    flexDirection: "column",
                    position: "fixed",
                    zIndex: 2000,
                    top: 0,
                    right: 0,
                    height: "100vh",
                    transition: "right 0.3s ease",
                    backgroundColor: "rgba(255, 255, 255, 0.4)",
                    // borderRadius: '16px',
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: "16px",
                  }}>
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "calc(100% - 290px)",
                      height: "100vh",
                      backgroundColor: "#00000030",
                      zIndex: -1,
                    }}
                    onClick={() => setOpen(false)}
                  />
                  <Sidebar
                    moduleKey={moduleKey}
                    showFullMenu={false}
                    onMenuItemClick={() => {
                      setOpen(false);
                    }}
                    mobile={true}
                    backroute={effectiveBackroute}
                    isHomePage={isHomePage}
                    firmOnboarded={firmOnboarded}
                    manufacturerSelected={selectedManufacturer}
                    onFirmModalShow={showFirmModal}
                    onManufacturerModalShow={showManufacturerModal}
                  />
                </Box>
              </Slide>
            </>
          )}
          {!isMobile && (loginUser || isPwa) && (
            <Box
              sx={{
                width: 300,
                minWidth: 300,
                height: "100%",
                position: "relative",
                zIndex: 1050,
                flexShrink: 0,
                overflowX: "auto",
                ml: 2,
              }}>
              <Sidebar
                showFullMenu={true}
                moduleKey={moduleKey}
                backroute={effectiveBackroute}
                isHomePage={isHomePage}
                onMenuItemClick={() => {
                  setOpen(false);
                }}
                mobile={false}
                firmOnboarded={firmOnboarded}
                manufacturerSelected={selectedManufacturer}
                onFirmModalShow={showFirmModal}
                onManufacturerModalShow={showManufacturerModal}
              />
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              pt: { xs: 0, md: 1 },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}>
            {!isMobile && effectiveTitle && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pb: 2,
                  px: 2,
                }}>
                <IconButton
                  onClick={handleBackNavigation}
                  sx={{ cursor: "pointer" }}
                  data-testid="main-layout-back-button"
                  data-navigation-target={navigationTarget}
                  className="main-layout-back-button">
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="h1" sx={{ ml: 2 }}>
                  {effectiveTitle}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                position: "relative",
                flexGrow: 1,
                overflow: open && "hidden",
                mb: "70px",
              }}>
              {children}
            </Box>
            {isMobile && (loginUser || isPwa) && TabConfig.length > 0 && (
              <BottomTabBar
                onTabChange={handleProtectedTabChange}
                onFirmModalShow={showFirmModal}
                onManufacturerModalShow={showManufacturerModal}
                tabConfig={TabConfig}
                firmOnboarded={firmOnboarded}
                manufacturerSelected={selectedManufacturer}
              />
            )}
          </Box>
        </Box>
      </Box>
      <FirmManufactureModal
        firmModal={firmModal}
        setFirmModal={setFirmModal}
        manufacturerModal={manufacturerModal}
        setManufacturerModal={setManufacturerModal}
      />
    </Box>
  );
};;

export default MainLayout;
